import React, { useRef, useState, useEffect, useCallback } from 'react';
import { usePutAssetMutation } from '../../features/api/apiSlice';
import * as maptilersdk from '@maptiler/sdk';
import { Modal, message } from 'antd';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import useClickOutside from '../../utils/useClickOutside';

const EditLocation = ({ isModalOpen, setModalOpen, selectedAsset, refetch }) => {
  const [updateAsset] = usePutAssetMutation();
  const mapContainerRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [marker, setMarker] = useState(null);
  const [localAsset, setLocalAsset] = useState(null);
  const [depth, setDepth] = useState('');
  const [altitude, setAltitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    latitude: null,
    longitude: null,
    depth: null,
    altitude: null,
  });

  const [originalValues, setOriginalValues] = useState({
    depth: '',
    altitude: '',
    longitude: '',
    latitude: '',
  });

  // Use click outside handler - Modal doesn't support refs, so we rely on selector
  useClickOutside({ current: null }, ['.ant-modal-content'], () => {
    setModalOpen(false);
  });

  // Validate fields - only check format if value is provided (all fields optional)
  const validateFields = useCallback(() => {
    const errors = {
      latitude: null,
      longitude: null,
      depth: null,
      altitude: null,
    };

    // Validate latitude format (optional field)
    if (latitude && latitude.trim() !== '' && isNaN(parseFloat(latitude))) {
      errors.latitude = 'Latitude must be a valid number';
    }

    // Validate longitude format (optional field)
    if (longitude && longitude.trim() !== '' && isNaN(parseFloat(longitude))) {
      errors.longitude = 'Longitude must be a valid number';
    }

    // Validate depth format (optional field)
    if (depth && depth.trim() !== '' && isNaN(parseFloat(depth))) {
      errors.depth = 'Depth must be a valid number';
    }

    // Validate altitude format (optional field)
    if (altitude && altitude.trim() !== '' && isNaN(parseFloat(altitude))) {
      errors.altitude = 'Altitude must be a valid number';
    }

    setFieldErrors(errors);
    const hasErrors = Object.values(errors).some((error) => error !== null);
    return hasErrors;
  }, [latitude, longitude, depth, altitude]);

  // Define initMap before it's used in useEffect
  const initMap = useCallback(() => {
    if (mapInstance || !mapContainerRef.current) return;

    maptilersdk.config.apiKey = process.env.REACT_APP_MAP_KEY;

    // Get coordinates from selectedAsset if available, otherwise use current state
    let centerLng = 0;
    let centerLat = 0;
    let zoom = 8;

    if (selectedAsset?.longitude !== undefined && selectedAsset?.longitude !== null) {
      centerLng = parseFloat(selectedAsset.longitude);
    } else if (longitude) {
      centerLng = parseFloat(longitude);
    }

    if (selectedAsset?.latitude !== undefined && selectedAsset?.latitude !== null) {
      centerLat = parseFloat(selectedAsset.latitude);
    } else if (latitude) {
      centerLat = parseFloat(latitude);
    }

    // If we have valid coordinates, use them; otherwise use a default location (Netherlands)
    if (isNaN(centerLng) || isNaN(centerLat) || (centerLng === 0 && centerLat === 0)) {
      centerLng = 4.9041; // Default to Netherlands
      centerLat = 52.3676;
      zoom = 7;
    }

    const map = new maptilersdk.Map({
      container: mapContainerRef.current,
      style: 'hybrid',
      center: [centerLng, centerLat],
      zoom: zoom,
    });

    setMapInstance(map);
    mapValidRef.current = true;
  }, [selectedAsset, longitude, latitude, mapInstance]);

  // Use refs to track state
  const clickHandlerSetupRef = useRef(false);
  const isMapClickUpdateRef = useRef(false);
  const mapValidRef = useRef(false);

  // Set up map click handler once when map is ready
  useEffect(() => {
    if (!mapInstance || clickHandlerSetupRef.current) return;

    // Remove any existing click handlers to avoid duplicates
    mapInstance.off('click');

    // Add click handler to update marker position
    mapInstance.on('click', (e) => {
      const [clickLng, clickLat] = e.lngLat.toArray();
      const lngFixed = parseFloat(clickLng.toFixed(6));
      const latFixed = parseFloat(clickLat.toFixed(6));
      const lngStr = lngFixed.toString();
      const latStr = latFixed.toString();

      // Set flag to indicate this is a map click update
      isMapClickUpdateRef.current = true;

      setLongitude(lngStr);
      setLatitude(latStr);
      setMarker((prevMarker) => {
        if (prevMarker) {
          prevMarker.setLngLat([lngFixed, latFixed]);
        }
        return prevMarker;
      });

      // Instant validation for map click - clear errors since values are valid
      setFieldErrors((prev) => ({
        ...prev,
        longitude: null,
        latitude: null,
      }));
    });

    clickHandlerSetupRef.current = true;

    return () => {
      if (mapInstance) {
        mapInstance.off('click');
        clickHandlerSetupRef.current = false;
      }
    };
  }, [mapInstance]);

  // First, set the values from selectedAsset
  useEffect(() => {
    if (isModalOpen && selectedAsset) {
      setLocalAsset(selectedAsset);
      const depthVal =
        selectedAsset.depth !== undefined && selectedAsset.depth !== null
          ? selectedAsset.depth.toString()
          : '';
      const altitudeVal =
        selectedAsset.altitude !== undefined && selectedAsset.altitude !== null
          ? selectedAsset.altitude.toString()
          : '';
      // Use actual values, not '0' as default
      const longVal =
        selectedAsset.longitude !== undefined && selectedAsset.longitude !== null
          ? selectedAsset.longitude.toString()
          : '';
      const latVal =
        selectedAsset.latitude !== undefined && selectedAsset.latitude !== null
          ? selectedAsset.latitude.toString()
          : '';

      setDepth(depthVal);
      setAltitude(altitudeVal);
      setLongitude(longVal);
      setLatitude(latVal);

      setOriginalValues({
        depth: depthVal,
        altitude: altitudeVal,
        longitude: longVal,
        latitude: latVal,
      });

      // Instant validation after setting initial values (all fields optional)
      const errors = {
        latitude: null,
        longitude: null,
        depth: null,
        altitude: null,
      };

      // Only validate format if value is provided
      if (latVal && latVal.trim() !== '' && isNaN(parseFloat(latVal))) {
        errors.latitude = 'Latitude must be a valid number';
      }

      if (longVal && longVal.trim() !== '' && isNaN(parseFloat(longVal))) {
        errors.longitude = 'Longitude must be a valid number';
      }

      if (depthVal && depthVal.trim() !== '' && isNaN(parseFloat(depthVal))) {
        errors.depth = 'Depth must be a valid number';
      }

      if (altitudeVal && altitudeVal.trim() !== '' && isNaN(parseFloat(altitudeVal))) {
        errors.altitude = 'Altitude must be a valid number';
      }

      setFieldErrors(errors);
    } else if (!isModalOpen) {
      // Reset validation state when modal closes
      setFieldErrors({
        latitude: null,
        longitude: null,
        depth: null,
        altitude: null,
      });
    }
  }, [isModalOpen, selectedAsset]);

  // Initialize map after we have coordinates
  useEffect(() => {
    if (isModalOpen && !mapInstance && mapContainerRef.current && selectedAsset) {
      initMap();
    }

    return () => {
      if (!isModalOpen) {
        // Clean up map and marker when modal closes
        mapValidRef.current = false;
        if (mapInstance) {
          try {
            mapInstance.remove();
          } catch (error) {
            console.warn('Error removing map:', error);
          }
          setMapInstance(null);
        }
        if (marker) {
          try {
            marker.remove();
          } catch (error) {
            console.warn('Error removing marker:', error);
          }
          setMarker(null);
        }
      }
    };
  }, [isModalOpen, mapInstance, selectedAsset, initMap]);

  // Create marker when we have valid coordinates and map is ready
  // Only create once when marker doesn't exist
  useEffect(() => {
    if (isModalOpen && mapInstance && longitude && latitude && !marker) {
      const lng = parseFloat(longitude);
      const lat = parseFloat(latitude);

      if (!isNaN(lng) && !isNaN(lat)) {
        const initialMarker = new maptilersdk.Marker({ color: '#1677ff' })
          .setLngLat([lng, lat])
          .addTo(mapInstance);
        setMarker(initialMarker);
      }
    }
  }, [isModalOpen, mapInstance, longitude, latitude, marker]);

  // Update marker position when coordinates change from input fields (not from map click)
  useEffect(() => {
    // Skip update if it's from a map click (marker already updated in click handler)
    if (isMapClickUpdateRef.current) {
      isMapClickUpdateRef.current = false;
      return;
    }

    if (isModalOpen && mapInstance && marker && longitude && latitude && mapValidRef.current) {
      const lng = parseFloat(longitude);
      const lat = parseFloat(latitude);

      if (!isNaN(lng) && !isNaN(lat)) {
        try {
          marker.setLngLat([lng, lat]);
          mapInstance.setCenter([lng, lat]);
        } catch (error) {
          console.warn('Error updating map center:', error);
          // If error occurs, mark map as invalid
          mapValidRef.current = false;
        }
      }
    }
  }, [isModalOpen, mapInstance, marker, longitude, latitude]);

  // Compute hasFormErrors from fieldErrors
  const hasFormErrors = Object.values(fieldErrors).some((error) => error !== null);

  // Validate fields whenever they change - instant validation
  useEffect(() => {
    if (isModalOpen) {
      validateFields();
    }
  }, [isModalOpen, latitude, longitude, depth, altitude, validateFields]);

  const handleSubmit = () => {
    console.log('handleSubmit', localAsset);
    if (!localAsset) return;
    const payload = {
      projectId: localAsset.project_id,
      assetId: localAsset.id,
    };

    if (depth !== originalValues.depth) {
      payload.depth = depth === '' ? null : parseFloat(depth);
    }

    if (altitude !== originalValues.altitude) {
      payload.altitude = altitude === '' ? null : parseFloat(altitude);
    }

    if (longitude !== originalValues.longitude) {
      payload.longitude = longitude === '' ? null : parseFloat(longitude);
    }

    if (latitude !== originalValues.latitude) {
      payload.latitude = latitude === '' ? null : parseFloat(latitude);
    }

    updateAsset(payload)
      .unwrap()
      .then(() => {
        refetch();
        setModalOpen(false);
      })
      .catch((error) => {
        console.error('Error updating asset:', error);
        message.error('Failed to update asset. Please try again.');
      });
  };

  const handleLongitudeChange = (e) => {
    const value = e.target.value;
    setLongitude(value);

    // Instant validation (optional field - only validate format)
    const errors = { ...fieldErrors };
    if (value && value.trim() !== '' && isNaN(parseFloat(value))) {
      errors.longitude = 'Longitude must be a valid number';
    } else {
      errors.longitude = null;
    }
    setFieldErrors(errors);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      try {
        if (marker) marker.setLngLat([numValue, parseFloat(latitude) || 0]);
        if (mapInstance && mapValidRef.current) {
          mapInstance.setCenter([numValue, parseFloat(latitude) || 0]);
        }
      } catch (error) {
        console.warn('Error updating map center:', error);
        mapValidRef.current = false;
      }
    }
  };

  const handleLatitudeChange = (e) => {
    const value = e.target.value;
    setLatitude(value);

    // Instant validation (optional field - only validate format)
    const errors = { ...fieldErrors };
    if (value && value.trim() !== '' && isNaN(parseFloat(value))) {
      errors.latitude = 'Latitude must be a valid number';
    } else {
      errors.latitude = null;
    }
    setFieldErrors(errors);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      try {
        if (marker) marker.setLngLat([parseFloat(longitude) || 0, numValue]);
        if (mapInstance && mapValidRef.current) {
          mapInstance.setCenter([parseFloat(longitude) || 0, numValue]);
        }
      } catch (error) {
        console.warn('Error updating map center:', error);
        mapValidRef.current = false;
      }
    }
  };

  const handleDepthChange = (e) => {
    const value = e.value;
    const valueStr = value === null ? '' : value.toString();
    setDepth(valueStr);

    // Instant validation (optional field - only validate format)
    const errors = { ...fieldErrors };
    if (valueStr && valueStr.trim() !== '' && isNaN(parseFloat(valueStr))) {
      errors.depth = 'Depth must be a valid number';
    } else {
      errors.depth = null;
    }
    setFieldErrors(errors);
  };

  const handleAltitudeChange = (e) => {
    const value = e.value;
    const valueStr = value === null ? '' : value.toString();
    setAltitude(valueStr);

    // Instant validation (optional field - only validate format)
    const errors = { ...fieldErrors };
    if (valueStr && valueStr.trim() !== '' && isNaN(parseFloat(valueStr))) {
      errors.altitude = 'Altitude must be a valid number';
    } else {
      errors.altitude = null;
    }
    setFieldErrors(errors);
  };

  const closeModal = () => {
    mapValidRef.current = false;
    setMapInstance(null);
    setMarker(null);
    setLocalAsset(null);
    setModalOpen(false);
  };

  const renderAssetName = () =>
    localAsset || selectedAsset ? `for ${(localAsset || selectedAsset)?.name}` : '';

  if (!isModalOpen) return null;

  return (
    <Modal
      title={`Edit Location ${renderAssetName()}`}
      open={isModalOpen}
      onCancel={closeModal}
      onOk={() => handleSubmit()}
      okText="Update Asset"
      cancelText="Cancel"
      okButtonProps={{
        disabled: hasFormErrors,
      }}
      centered
      maskClosable={false}
      styles={{
        body: {
          maxHeight: 'calc(90vh - 120px)',
          overflow: 'hidden',
        },
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
          fontWeight: 600,
          fontSize: 18,
        },
        footer: {
          display: 'flex',
          justifyContent: 'end',
          gap: 16,
          alignItems: 'center',
          padding: 8,
        },
      }}
    >
      <div
        style={{
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          padding: 24,
        }}
      >
        <div
          ref={mapContainerRef}
          style={{ width: '100%', height: '400px', marginBottom: '1rem' }}
        />

        <div className="p-fluid p-formgrid p-grid" style={{ gap: '2rem' }}>
          <div className="p-field p-col-12 p-md-6">
            <label htmlFor="latitude">Latitude</label>
            <InputText
              id="latitude"
              value={latitude}
              onChange={handleLatitudeChange}
              keyfilter={/^-?\d*\.?\d*$/}
              className={fieldErrors.latitude ? 'p-invalid' : ''}
              style={fieldErrors.latitude ? { borderColor: '#dc3545' } : {}}
            />
            {fieldErrors.latitude && (
              <small
                className="p-error"
                style={{ display: 'block', marginTop: '4px', color: '#dc3545' }}
              >
                {fieldErrors.latitude}
              </small>
            )}
          </div>
          <div className="p-field p-col-12 p-md-6">
            <label htmlFor="longitude">Longitude</label>
            <InputText
              id="longitude"
              value={longitude}
              onChange={handleLongitudeChange}
              keyfilter={/^-?\d*\.?\d*$/}
              className={fieldErrors.longitude ? 'p-invalid' : ''}
              style={fieldErrors.longitude ? { borderColor: '#dc3545' } : {}}
            />
            {fieldErrors.longitude && (
              <small
                className="p-error"
                style={{ display: 'block', marginTop: '4px', color: '#dc3545' }}
              >
                {fieldErrors.longitude}
              </small>
            )}
          </div>
          <div className="p-field p-col-12">
            <label htmlFor="depth">Depth (m)</label>
            <InputNumber
              id="depth"
              value={depth === '' ? null : parseFloat(depth)}
              onValueChange={handleDepthChange}
              mode="decimal"
              min={0}
              style={{ width: '100%', ...(fieldErrors.depth ? { borderColor: '#dc3545' } : {}) }}
              allowEmpty={true}
              className={fieldErrors.depth ? 'p-invalid' : ''}
            />
            {fieldErrors.depth && (
              <small
                className="p-error"
                style={{ display: 'block', marginTop: '4px', color: '#dc3545' }}
              >
                {fieldErrors.depth}
              </small>
            )}
          </div>
          <div className="p-field p-col-12">
            <label htmlFor="altitude">Altitude (m)</label>
            <InputNumber
              id="altitude"
              value={altitude === '' ? null : parseFloat(altitude)}
              onValueChange={handleAltitudeChange}
              mode="decimal"
              min={0}
              style={{ width: '100%', ...(fieldErrors.altitude ? { borderColor: '#dc3545' } : {}) }}
              allowEmpty={true}
              className={fieldErrors.altitude ? 'p-invalid' : ''}
            />
            {fieldErrors.altitude && (
              <small
                className="p-error"
                style={{ display: 'block', marginTop: '4px', color: '#dc3545' }}
              >
                {fieldErrors.altitude}
              </small>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditLocation;
